import { useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";

import { useMode } from "@/shared/api";
import { ROUTES } from "@/shared/config";
import { flipFrom } from "@/shared/lib";
import { Icon, type IconName } from "@/shared/ui";

/**
 * Нижняя навигация. Пять пунктов на всех экранах без исключения, включая
 * состояния и оверлеи; центральный остаётся действием «пополнить», а не
 * разделом — так он нарисован во всех пяти экранах пакета дизайна.
 *
 * Ширина панели резиновая, а не 358 как в макете: экран приложения тянется
 * до 430, и панель фиксированной ширины повисла бы посреди с полями по краям.
 * Боковой отступ задаёт AppLayout через `--nav-inset`.
 *
 * Высота — одна на все экраны (`--nav-height`). В пакете дизайна она разная
 * на каждом из пяти (64/56/55/62): это шум от пяти отдельно снятых источников,
 * а не замысел, и переносить его значило бы дёргать панель при переходах.
 *
 * Движение (FE-13): точка активного раздела не гаснет и не зажигается на
 * новом месте, а переезжает — панель остаётся одним объектом, а не пятью
 * независимыми лампочками.
 */

interface Item {
  to: string;
  icon: IconName;
  labelKey: string;
}

const ITEMS: readonly Item[] = [
  { to: ROUTES.home, icon: "house", labelKey: "nav.home" },
  // Робот — буквально предмет продажи, и в наборе он есть.
  { to: ROUTES.robots, icon: "robot", labelKey: "nav.robots" },
  // Пульс вместо «списка»: лента — это поток результатов, а не перечень.
  { to: ROUTES.activity, icon: "pulse", labelKey: "nav.activity" },
  { to: ROUTES.profile, icon: "userCircle", labelKey: "nav.profile" },
];

export function BottomNav() {
  const { t } = useTranslation();
  const { isDemo } = useMode();

  return (
    <nav
      className={[
        "flex h-[var(--nav-height)] w-full items-center justify-between",
        "rounded-xl border border-border-subtle bg-[image:var(--surface-nav)] px-2",
        "shadow-[var(--shadow-nav),var(--shadow-inset-top)]",
      ].join(" ")}
      aria-label={t("nav.home")}
    >
      <NavItem item={ITEMS[0]} />
      <NavItem item={ITEMS[1]} />

      {/* Центральный пункт — действие «пополнить», а не раздел: в макете это
          кнопка 44×44 на акцентном фоне без подписи.

          Намерение объявлено явно: утилита `press` висит на внутреннем
          квадрате, а не на ссылке, и делегат счёл бы касание нажатием, а не
          переходом (FE-14). */}
      {/* В демо пополнять нечего — §3.1 запрещает переводить в демо реальные
          средства, — поэтому центральное действие ведёт на демо-счёт, где
          живёт единственная осмысленная в песочнице операция: сброс. */}
      <NavLink
        to={isDemo ? ROUTES.wallet : ROUTES.depositAsset}
        data-haptic="nav"
        aria-label={t(isDemo ? "demo.wallet.title" : "nav.deposit")}
        className="flex h-[56px] flex-1 flex-col items-center justify-center p-1.5"
      >
        <span
          className={[
            "press flex size-[var(--control-fab)] items-center justify-center rounded-[14px]",
            "bg-[image:var(--surface-accent)] text-text-inverse",
            "shadow-[inset_0_1px_rgb(255_255_255/0.45)]",
          ].join(" ")}
        >
          <Icon name={isDemo ? "coinStack" : "plus"} size={22} weight={2.4} />
        </span>
      </NavLink>

      <NavItem item={ITEMS[2]} />
      <NavItem item={ITEMS[3]} />
    </nav>
  );
}

function NavItem({ item }: { item: Item | undefined }) {
  const { t } = useTranslation();
  if (!item) return null;

  return (
    <NavLink
      to={item.to}
      end={item.to === ROUTES.home}
      className={({ isActive }) =>
        [
          "press relative flex h-[56px] flex-1 flex-col items-center justify-center gap-1 p-1.5",
          "transition-colors duration-[var(--motion-fast)] ease-standard",
          isActive ? "text-action-primary" : "text-text-tertiary",
        ].join(" ")
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            name={item.icon}
            size={22}
            className={[
              "transition-transform duration-[var(--motion-base)] ease-spring",
              isActive ? "scale-110" : "scale-100",
            ].join(" ")}
          />
          <span className="text-micro font-medium">{t(item.labelKey)}</span>
          {isActive ? <ActiveDot /> : null}
        </>
      )}
    </NavLink>
  );
}

/**
 * Точка под активным пунктом. Между пунктами она переезжает по FLIP:
 * геометрия предыдущей точки запоминается при её размонтировании, новая
 * стартует оттуда. React гарантирует нужный порядок — cleanup layout-эффекта
 * снятого пункта выполняется до layout-эффекта появившегося.
 */
let lastDotRect: DOMRect | undefined;

function ActiveDot() {
  const ref = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    flipFrom(element, lastDotRect);

    return () => {
      lastDotRect = element.getBoundingClientRect();
    };
  });

  return (
    <span
      ref={ref}
      aria-hidden="true"
      className="absolute bottom-0.5 size-1 rounded-full bg-action-primary shadow-[0_0_var(--glow-blur-sm)_var(--color-action-primary)]"
    />
  );
}
